/**
 * OverrideInterestDialog — manually set interest level with reason.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, Input } from '@/components/common';
import { useOverrideInterest } from '../api/leads';
import {
  INTEREST_LEVEL_LABEL_MAP,
  INTEREST_LEVEL_COLOR_MAP,
  type InterestLevel,
} from '../config/ui';

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const RadioCard = styled.label<{ $selected: boolean; $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 2px solid ${({ $selected, $color }) => $selected ? $color : '#e2e8f0'};
  background: ${({ $selected, $color }) => $selected ? `${$color}15` : 'transparent'};
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  color: ${({ $selected, $color }) => $selected ? $color : '#64748b'};
  transition: all 0.15s ease;
  &:hover {
    border-color: ${({ $color }) => $color};
  }
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
}

const LEVELS: InterestLevel[] = ['cold', 'warm', 'hot'];

export const OverrideInterestDialog: React.FC<Props> = ({ isOpen, onClose, leadId }) => {
  const [selected, setSelected] = useState<InterestLevel>('warm');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const overrideMutation = useOverrideInterest();

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    await overrideMutation.mutateAsync({
      id: leadId,
      payload: {
        interest_level: selected,
        reason: reason.trim(),
        expires_at: expiresAt || null,
      },
    });
    onClose();
    setReason('');
    setExpiresAt('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Override Interest Level"
      size="md"
      data-testid="override-interest-dialog"
    >
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
        Manually set the interest level. This overrides the computed score until the expiry date or until recomputed.
      </p>

      <RadioGroup>
        {LEVELS.map((level) => {
          const color = INTEREST_LEVEL_COLOR_MAP[level];
          const label = INTEREST_LEVEL_LABEL_MAP[level];
          return (
            <RadioCard
              key={level}
              $selected={selected === level}
              $color={color}
              data-testid={`interest-option-${level}`}
            >
              <HiddenRadio
                type="radio"
                name="interest_level"
                value={level}
                checked={selected === level}
                onChange={() => setSelected(level)}
              />
              {label}
            </RadioCard>
          );
        })}
      </RadioGroup>

      <FieldGroup>
        <Input
          label="Reason *"
          placeholder="Why are you overriding the interest level?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="override-reason-input"
          required
        />
      </FieldGroup>

      <FieldGroup>
        <Input
          label="Override Expiry Date (optional)"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          data-testid="override-expiry-input"
        />
      </FieldGroup>

      {overrideMutation.isError && (
        <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>
          Override failed. Please try again.
        </p>
      )}

      <ModalActions>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          data-testid="override-submit-btn"
          disabled={!reason.trim() || overrideMutation.isPending}
          onClick={handleSubmit}
        >
          {overrideMutation.isPending ? 'Saving…' : 'Set Override'}
        </Button>
      </ModalActions>
    </Modal>
  );
};
